import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAccount } from 'wagmi';
import { useAppKit } from '@reown/appkit/react';
import { useNavigate } from 'react-router-dom';
import { bsc } from '@reown/appkit/networks';
import toast from 'react-hot-toast';
import NavHeader from '../components/NavHeader';
import FooterSection from '../components/FooterSection';
import GameSelector from '../components/games/GameSelector';
import GameInfoPanel from '../components/games/GameInfoPanel';
import NumberPickerStrike from '../components/games/NumberPickerStrike';
import NumberPickerVault from '../components/games/NumberPickerVault';
import NumberPickerQuantum from '../components/games/NumberPickerQuantum';
import NumberPickerApex from '../components/games/NumberPickerApex';
// import NumberPickerSummit from '../components/games/NumberPickerSummit';
import PickAnimationPopup from '../components/games/PickAnimationPopup';
import { getGameConfig, getDefaultGameId, saveLastGameId, isGamePlayEnabled } from '../data/games';
import { formatGameNumber, getPickSpan, winNumberFromTxHashDigits } from '../api/gameApi';
import { ethers } from 'ethers';
import { stringToHex, parseUnits, formatUnits, zeroAddress, zeroHash, pad } from 'viem';
import { readContract, writeContract, waitForTransactionReceipt } from 'wagmi/actions';
import { gameAddress, game2Address, coreVaultAddress, refTopAddr } from '../utils/addressHelpers.ts';
import { config } from '../config.jsx';
import gameAbi from '../config/gameAbi.json';
import vaultAbi from '../config/vaultAbi.json';
import { GENERATE_WINNER_API_URL, VAULT_WINNER_EVENT_TOPIC0 } from '../utils/constants.ts';
import { fetchVaultWinnerPayoutsFromTxHash, addBoughtData } from '../api/playerApi.js';
import { getStoredReferralCode } from '../components/RefLinkCapture.js';

/** Numbers in range that are not already selected and not already on-chain this round. */
function buildEligibleNumberPool(game, selectedNumbers, onChainPickIds) {
  if (!game?.numberRange) return [];
  const fromChain = onChainPickIds.map((id) => (typeof id === 'bigint' ? Number(id) : Number(id)));
  const exclude = new Set([...selectedNumbers, ...fromChain]);
  const { min, max } = game.numberRange;
  const pool = [];
  for (let n = min; n <= max; n += 1) {
    if (!exclude.has(n)) pool.push(n);
  }
  return pool;
}

function pickIdToNumber(id) {
  return typeof id === 'bigint' ? Number(id) : Number(id);
}

function sortPickIdsByNumber(ids) {
  if (!Array.isArray(ids) || ids.length === 0) return [];
  return [...ids].sort((a, b) => pickIdToNumber(a) - pickIdToNumber(b));
}

/** Matches Solidity `bytes32(bytes(string))`: UTF-8, right-padded to 32 bytes. */
function referralTextToBytes32(str) {
  const s = String(str ?? '').trim();
  if (!s) return zeroHash;
  const hex = stringToHex(s);
  try {
    return pad(hex, { size: 32, dir: 'right' });
  } catch {
    throw new Error('Referral code is too long (max 32 bytes UTF-8)');
  }
}

function Games() {
  const { address, chainId } = useAccount();
  const { open } = useAppKit();
  const navigate = useNavigate();
  const [activeGame, setActiveGame] = useState(getDefaultGameId());
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [placing, setPlacing] = useState(false);
  const [popupNumber, setPopupNumber] = useState(null);
  const [creditWarning, setCreditWarning] = useState(null);
  const [roundId, setRoundId] = useState(null);
  const [soldTickets, setSoldTickets] = useState(null);
  const [roundLoading, setRoundLoading] = useState(true);
  const [roundError, setRoundError] = useState(null);
  const [userTicketCount, setUserTicketCount] = useState(null);
  const [userTicketCountLoading, setUserTicketCountLoading] = useState(false);
  const [onChainPickIds, setOnChainPickIds] = useState([]);
  const [syncingRoundOutcome, setSyncingRoundOutcome] = useState(false);
  const [generatingWinner, setGeneratingWinner] = useState(false);
  const [winnerRevealBusy, setWinnerRevealBusy] = useState(false);
  const [winnerRevealPayload, setWinnerRevealPayload] = useState(null);
  /** Game + round shown in the winner modal header (set when round completes). */
  const [winnerRevealMeta, setWinnerRevealMeta] = useState(null);
  const [winnerDetailTab, setWinnerDetailTab] = useState('winner');
  const [rangeFromInput, setRangeFromInput] = useState('');
  const [rangeToInput, setRangeToInput] = useState('');
  /** Pick Your Numbers: "range" vs "numbers" panel */
  const [pickYourNumbersTab, setPickYourNumbersTab] = useState('numbers');
  const [vaultCreditWei, setVaultCreditWei] = useState(null);
  const [vaultCreditLoading, setVaultCreditLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (generatingWinner) setWinnerDetailTab('winner');
  }, [generatingWinner]);

  const game = getGameConfig(activeGame);
  const canPlayActiveGame = isGamePlayEnabled(activeGame);
  const activeGameAddress = activeGame === 'apex2' ? game2Address : gameAddress;
  const loadVaultCredit = useCallback(async () => {
    if (!address) {
      setVaultCreditWei(null);
      setVaultCreditLoading(false);
      return;
    }
    setVaultCreditLoading(true);
    try {
      const cookieRef = getStoredReferralCode();
      const refStr =
        cookieRef && typeof cookieRef === 'string' && cookieRef.length >= 4 ? cookieRef : '';
      const refBytes32 = referralTextToBytes32(refStr);
      const data = await readContract(config, {
        address: coreVaultAddress,
        abi: vaultAbi,
        functionName: 'getUserDatas',
        args: [address, refBytes32],
      });
      if (mountedRef.current) setVaultCreditWei(data[0][1]);
    } catch (e) {
      console.error('Games vault credit load failed:', e);
      if (mountedRef.current) setVaultCreditWei(null);
    } finally {
      if (mountedRef.current) setVaultCreditLoading(false);
    }
  }, [address]);

  useEffect(() => {
    loadVaultCredit();
  }, [loadVaultCredit, chainId]);

  const vaultCreditUsdLabel = useMemo(() => {
    if (!address) return null;
    if (vaultCreditLoading) return '…';
    if (vaultCreditWei == null) return '—';
    try {
      const n = Number(formatUnits(vaultCreditWei, 18));
      if (!Number.isFinite(n)) return '—';
      return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
    } catch {
      return '—';
    }
  }, [address, vaultCreditLoading, vaultCreditWei]);

  /** Max numbers allowed in a single `buyTickets` call (not a cumulative round cap). */
  const maxPicksPerBet = useMemo(() => Math.max(0, game?.maxNumbersPerTicket ?? 0), [game]);

  /** Current slip only — on-chain picks this round are shown separately. */
  const picksThisBetCount = selectedNumbers.length;

  const fetchCurrentRound = useCallback(async () => {
    setUserTicketCountLoading(true);
    setRoundLoading(true);
    setRoundError(null);
    setOnChainPickIds([]);
    const gameTypeBytes32 = stringToHex(activeGame, { size: 32 });
    const userForRead = address ?? zeroAddress;
    try {
      const result = await readContract(config, {
        address: activeGameAddress,
        abi: gameAbi,
        functionName: 'getDatas',
        args: [gameTypeBytes32, userForRead],
        chainId: bsc.id,
      });
      const [rid, sold, count, ids] = result;
      setRoundId(Number(rid));
      setSoldTickets(sold);
      setUserTicketCount(count);
      setOnChainPickIds(sortPickIdsByNumber(ids));
      setRoundLoading(false);
      setUserTicketCountLoading(false);
    } catch (e) {
      setRoundError(e?.shortMessage || e?.message || 'Could not load round');
      setRoundId(null);
      setSoldTickets(null);
      setUserTicketCount(null);
      setOnChainPickIds([]);
      setRoundLoading(false);
      setUserTicketCountLoading(false);
    }
  }, [activeGame, activeGameAddress, address, chainId]);

  useEffect(() => {
    fetchCurrentRound();
    const t = setInterval(fetchCurrentRound, 30000);  // per 30 seconds
    return () => clearInterval(t);
  }, [fetchCurrentRound]);

  useEffect(() => {
    if (!isGamePlayEnabled(activeGame)) {
      setActiveGame(getDefaultGameId());
      return;
    }
    saveLastGameId(activeGame);
    setSelectedNumbers([]);
    setRangeFromInput('');
    setRangeToInput('');
    setPickYourNumbersTab('numbers');
  }, [activeGame]);

  useEffect(() => {
    setSelectedNumbers((prev) => {
      const cap = game?.maxNumbersPerTicket ?? 0;
      if (prev.length <= cap) return prev;
      return prev.slice(0, cap);
    });
  }, [game?.maxNumbersPerTicket]);

  const showPopup = useCallback((num) => {
    setPopupNumber(num);
  }, []);

  const handlePopupComplete = useCallback(() => {
    setPopupNumber(null);
  }, []);

  const closeWinnerRevealModal = useCallback(() => {
    setGeneratingWinner(false);
    setWinnerRevealBusy(false);
    setWinnerRevealPayload(null);
    setWinnerRevealMeta(null);
  }, []);

  const copyToClipboard = useCallback((text, successMessage = 'Copied to clipboard') => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(successMessage),
      () => toast.error('Copy failed'),
    );
  }, []);

  const handleSelectNumber = useCallback(
    (num) => {
      setSelectedNumbers((prev) => {
        if (prev.includes(num)) return prev;
        if (prev.length >= maxPicksPerBet) {
          toast.error(`Pick at most ${maxPicksPerBet} numbers per purchase`);
          return prev;
        }
        showPopup(num);
        return [...prev, num];
      });
    },
    [showPopup, maxPicksPerBet],
  );

  const handleRemoveNumber = useCallback((num) => {
    setSelectedNumbers((prev) => prev.filter((n) => n !== num));
  }, []);

  const handleClearNumbers = useCallback(() => {
    setSelectedNumbers([]);
  }, []);

  const handlePickRandom = useCallback(() => {
    if (maxPicksPerBet <= 0) {
      toast.error('This game has no valid pick limit configured');
      return;
    }
    if (selectedNumbers.length >= maxPicksPerBet) {
      toast.error(`Pick at most ${maxPicksPerBet} numbers per purchase`);
      return;
    }
    const pool = buildEligibleNumberPool(game, selectedNumbers, onChainPickIds);
    if (pool.length === 0) {
      toast.error('Not enough free numbers left in the range');
      return;
    }
    const num = pool[Math.floor(Math.random() * pool.length)];
    handleSelectNumber(num);
  }, [selectedNumbers, game, onChainPickIds, handleSelectNumber, maxPicksPerBet]);

  const handlePickAllRandom = useCallback(() => {
    const cap = maxPicksPerBet;
    if (cap <= 0) {
      toast.error('This game has no valid pick limit configured');
      return;
    }
    const remaining = cap - selectedNumbers.length;
    if (remaining <= 0) {
      toast.error(`Pick at most ${cap} numbers per purchase`);
      return;
    }

    const pool = buildEligibleNumberPool(game, selectedNumbers, onChainPickIds);
    if (pool.length < remaining) {
      toast.error('Not enough free numbers left in the range');
      return;
    }

    const picked = [];
    const bag = [...pool];
    for (let i = 0; i < remaining; i += 1) {
      const j = Math.floor(Math.random() * bag.length);
      picked.push(bag[j]);
      bag.splice(j, 1);
    }

    picked.forEach((num, i) => {
      setTimeout(() => showPopup(num), i * 150);
    });
    setSelectedNumbers((prev) => [...prev, ...picked]);
  }, [selectedNumbers, game, onChainPickIds, showPopup, maxPicksPerBet]);

  const handleApplyRange = useCallback(() => {
    if (!game?.numberRange) return;
    if (maxPicksPerBet <= 0) {
      toast.error('This game has no valid pick limit configured');
      return;
    }
    const { min: gMin, max: gMax } = game.numberRange;
    const fromRaw = String(rangeFromInput).trim();
    const toRaw = String(rangeToInput).trim();
    if (fromRaw === '' || toRaw === '') {
      toast.error('Enter both From and To');
      return;
    }
    const fromParsed = parseInt(fromRaw, 10);
    const toParsed = parseInt(toRaw, 10);
    if (!Number.isFinite(fromParsed) || !Number.isFinite(toParsed)) {
      toast.error('Enter valid whole numbers for From and To');
      return;
    }
    let lo = Math.min(fromParsed, toParsed);
    let hi = Math.max(fromParsed, toParsed);
    if (lo < gMin || hi > gMax) {
      toast.error(`Numbers must be between ${gMin} and ${gMax}`);
      return;
    }
    const chainNums = onChainPickIds.map(pickIdToNumber);
    const exclude = new Set([...selectedNumbers, ...chainNums]);
    const toAdd = [];
    for (let n = lo; n <= hi; n += 1) {
      if (!exclude.has(n)) toAdd.push(n);
    }
    if (toAdd.length === 0) {
      toast.error('No new numbers in that range (already on your slip or on-chain)');
      return;
    }
    if (toAdd.length > maxPicksPerBet) {
      toast.error(`Range width cannot exceed ${maxPicksPerBet} numbers per purchase`);
      return;
    }
    const remaining = maxPicksPerBet - selectedNumbers.length;
    if (remaining <= 0) {
      toast.error(`Pick at most ${maxPicksPerBet} numbers per purchase`);
      return;
    }
    if (toAdd.length > remaining) {
      toast.error(
        `That range adds ${toAdd.length} new numbers after skipping taken picks; you can add at most ${remaining} more (max ${maxPicksPerBet} per purchase).`,
      );
      return;
    }
    toAdd.forEach((num, i) => {
      setTimeout(() => showPopup(num), i * 80);
    });
    setSelectedNumbers((prev) => [...new Set([...prev, ...toAdd])].sort((a, b) => a - b));
    setRangeFromInput('');
    setRangeToInput('');
    toast.success(`Added ${toAdd.length} number${toAdd.length === 1 ? '' : 's'}`);
  }, [
    game,
    maxPicksPerBet,
    selectedNumbers,
    onChainPickIds,
    rangeFromInput,
    rangeToInput,
    showPopup,
  ]);

  const handlePlaceBet = async () => {
    if (!canPlayActiveGame) {
      toast.error('This game is not open for play yet');
      return;
    }
    if (!address) {
      open();
      return;
    }
    if (chainId != null && chainId !== bsc.id) {
      toast.error('Please switch to BNB Smart Chain Mainnet');
      return;
    }
    if (maxPicksPerBet <= 0) {
      toast.error('This game has no valid pick limit configured');
      return;
    }
    if (selectedNumbers.length === 0) {
      toast.error('Please select at least one number');
      return;
    }

    const betTotalUsd = selectedNumbers.length * (game?.entryFee || 0);
    let creditWei;
    let displayAddress;
    try {
      const cookieRef = getStoredReferralCode();
      const refStr =
        cookieRef && typeof cookieRef === 'string' && cookieRef.length >= 4 ? cookieRef : '';
      const refBytes32 = referralTextToBytes32(refStr);
      const data = await readContract(config, {
        address: coreVaultAddress,
        abi: vaultAbi,
        functionName: 'getUserDatas',
        args: [address, refBytes32]
      });
      creditWei = data[0][1];
      let rawUpline = data[1]?.[0];
      if(rawUpline === '0x0000000000000000000000000000000000000000') {
        rawUpline = data[1]?.[1];
      }
      const uplineStr = typeof rawUpline === 'string' ? rawUpline : '';
      const hasUpline = uplineStr !== '' && uplineStr.toLowerCase() !== zeroAddress.toLowerCase();
      displayAddress = hasUpline ? uplineStr : refTopAddr;
    } catch {
      toast.error('Could not load vault credit. Try again.');
      return;
    }

    const requiredWei = parseUnits(String(betTotalUsd), 18);
    if (creditWei < requiredWei) {
      const availableUsd = Number(formatUnits(creditWei, 18)).toLocaleString(undefined, {
        maximumFractionDigits: 6,
      });
      setCreditWarning({
        requiredUsd: betTotalUsd.toFixed(2),
        availableUsd,
      });
      return;
    }

    const betGameId = activeGame;
    const betGameAddress = betGameId === 'apex2' ? game2Address : gameAddress;
    const betGameTypeBytes32 = stringToHex(betGameId, { size: 32 });
    const betSpan = getPickSpan(betGameId);
    const betMin = getGameConfig(betGameId)?.numberRange?.min ?? 0;

    setPlacing(true);
    let txHash;
    try {
      const numbersArg = selectedNumbers.map((n) => Number(n));
      txHash = await writeContract(config, {
        address: betGameAddress,
        abi: gameAbi,
        functionName: 'buyTickets',
        args: [displayAddress, betGameTypeBytes32, numbersArg],
      });
      await waitForTransactionReceipt(config, { hash: txHash });
      try {
        await addBoughtData({
          txHash: String(txHash),
          timestamp: Math.floor(Date.now() / 1000),
          numbersArg,
          betGameTypeBytes32: String(betGameTypeBytes32),
          address: String(address),
          roundId: String(roundId),
        });
      } catch (err) {
        console.warn('addBoughtData:', err?.message || err);
      }
      toast.success('Tickets purchased on-chain');
      handleClearNumbers();
      await fetchCurrentRound();
      await loadVaultCredit();
    } catch (err) {
      toast.error(err?.shortMessage || err?.message || 'Transaction failed');
      return;
    } finally {
      setPlacing(false);
    }

    if (!mountedRef.current || !txHash) return;

    setSyncingRoundOutcome(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 6000));
      if (!mountedRef.current) return;

      const result = await readContract(config, {
        address: betGameAddress,
        abi: gameAbi,
        functionName: 'getDatas',
        args: [betGameTypeBytes32, address],
        chainId: bsc.id,
      });
      if (!mountedRef.current) return;

      const [rid, sold] = result;
      const winNumber =
        betSpan > 0 ? winNumberFromTxHashDigits(String(txHash), betSpan) + betMin : betMin;

      if (betSpan > 0 && Number(sold) === betSpan) {
        const metaGame = getGameConfig(betGameId);
        const roundIdNum = typeof rid === 'bigint' ? Number(rid) : Number(rid);
        setWinnerRevealMeta({
          gameId: betGameId,
          gameName: metaGame?.name ?? metaGame?.shortName ?? betGameId,
          roundId: Number.isFinite(roundIdNum) ? roundIdNum : null,
        });
        setGeneratingWinner(true);
        setWinnerRevealBusy(true);
        setWinnerRevealPayload(null);
        const buyTxStr = String(txHash);
        try {
          const res = await fetch(GENERATE_WINNER_API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              gameType: betGameTypeBytes32,
              winNumber,
              txHash: String(txHash),
              roundId: String(roundId),
            }),
          });
          if (!mountedRef.current) return;

          let payoutTxHash = buyTxStr;
          let apiOk = res.ok;
          let bodyText = '';
          try {
            bodyText = await res.text();
          } catch {
            /* ignore */
          }
          try {
            const parsed = bodyText ? JSON.parse(bodyText) : null;
            if (parsed && typeof parsed === 'object') {
              const h = parsed.txHash ?? parsed.txHash ?? parsed.hash;
              if (typeof h === 'string' && h.startsWith('0x')) payoutTxHash = h;
            }
          } catch {
            /* not JSON */
          }
          if (!res.ok) {
            console.warn('generateWinner API:', res.status, bodyText?.slice(0, 500) || '');
          }

          let chain = await fetchVaultWinnerPayoutsFromTxHash(payoutTxHash);
          if (
            chain.rows.length === 0 &&
            payoutTxHash.toLowerCase() !== buyTxStr.toLowerCase()
          ) {
            const fallback = await fetchVaultWinnerPayoutsFromTxHash(buyTxStr);
            if (fallback.rows.length > 0) {
              chain = fallback;
              payoutTxHash = buyTxStr;
            }
          }

          if (mountedRef.current) {
            setWinnerRevealPayload({
              payoutTxHash,
              buyTxHash: buyTxStr,
              primary: chain.primary,
              neighbors: chain.neighbors,
              allRows: chain.rows,
              chainError: chain.error,
              apiOk,
            });
          }
        } catch (e) {
          console.error('generateWinner request failed', e);
          let chain = {
            rows: [],
            primary: null,
            neighbors: [],
            error: null,
          };
          try {
            chain = await fetchVaultWinnerPayoutsFromTxHash(buyTxStr);
          } catch {
            /* ignore */
          }
          if (mountedRef.current) {
            setWinnerRevealPayload({
              payoutTxHash: buyTxStr,
              buyTxHash: buyTxStr,
              primary: chain.primary,
              neighbors: chain.neighbors,
              allRows: chain.rows,
              chainError: chain.error || e?.message || 'request_failed',
              apiOk: false,
            });
          }
        } finally {
          if (mountedRef.current) setWinnerRevealBusy(false);
        }
      }
    } catch (e) {
      console.warn('post-bet round sync failed', e);
    } finally {
      if (mountedRef.current) setSyncingRoundOutcome(false);
    }
  };

  const blockedPickNumbers = useMemo(() => {
    const fromChain = onChainPickIds.map((id) => (typeof id === 'bigint' ? Number(id) : Number(id)));
    return new Set([...selectedNumbers, ...fromChain]);
  }, [selectedNumbers, onChainPickIds]);

  const renderNumberPicker = useMemo(() => {
    const props = {
      game,
      selectedNumbers,
      onSelect: handleSelectNumber,
      onRemove: handleRemoveNumber,
      blockedPickNumbers,
      maxSelectableNumbers: maxPicksPerBet,
    };
    switch (activeGame) {
      case 'strike': return <NumberPickerStrike {...props} />;
      case 'vault': return <NumberPickerVault {...props} />;
      case 'quantum': return <NumberPickerQuantum {...props} />;
      case 'apex': return <NumberPickerApex {...props} />;
      case 'apex2': return <NumberPickerApex {...props} />;
      // case 'summit': return <NumberPickerSummit {...props} />;
      default: return null;
    }
  }, [activeGame, game, selectedNumbers, handleSelectNumber, handleRemoveNumber, blockedPickNumbers, maxPicksPerBet]);

  const totalCost = selectedNumbers.length * (game?.entryFee || 0);
  const isSummit = activeGame === 'summit';
  const isAtMax = selectedNumbers.length >= maxPicksPerBet;

  const onChainTicketsLabel = useMemo(() => {
    if (!address) return '—';
    if (roundLoading || userTicketCountLoading) return '…';
    if (userTicketCount === null || userTicketCount === undefined) return '—';
    return typeof userTicketCount === 'bigint'
      ? userTicketCount.toLocaleString()
      : Number(userTicketCount).toLocaleString();
  }, [address, roundLoading, userTicketCountLoading, userTicketCount]);

  const onChainGetNumbersSection = useMemo(
    () => (
      <div className="mt-4 border-t border-hash-gold/10 pt-4">
        <div className="text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
          All picks (on-chain)
        </div>
        {roundLoading ? (
          <p className="text-center text-xs text-gray-500">…</p>
        ) : onChainPickIds.length === 0 ? (
          <p className="text-center text-xs text-gray-500">No picks yet</p>
        ) : (
          <div className="flex max-h-32 flex-wrap justify-center gap-1.5 overflow-y-auto pr-0.5 sm:max-h-36 lg:max-h-40">
            {onChainPickIds.map((id, idx) => (
              <span
                key={`getNumbers-${idx}-${String(id)}`}
                className="inline-flex items-center justify-center rounded-md border border-hash-gold/25 bg-dark-surface/60 px-2 py-1 font-mono text-[11px] font-semibold text-hash-gold tabular-nums sm:text-xs"
              >
                {formatGameNumber(activeGame, id)}
              </span>
            ))}
          </div>
        )}
      </div>
    ),
    [roundLoading, onChainPickIds, activeGame],
  );

  const bscExplorerBase =
    bsc?.blockExplorers?.default?.url ?? 'https://bscscan.com';

  const winnerRevealTsv = useMemo(() => {
    const p = winnerRevealPayload;
    if (!p?.allRows?.length || !p.payoutTxHash) return '';
    const tx = p.payoutTxHash;
    const fmt = (w) => {
      try {
        const n = Number(ethers.utils.formatUnits(String(w || '0'), 18));
        return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 6 }) : '—';
      } catch {
        return '—';
      }
    };
    return [
      'address\tamount_usdt\ttransaction_hash',
      ...p.allRows.map((r) => `${r.walletAddress}\t${fmt(r.rewardWeiStr)}\t${tx}`),
    ].join('\n');
  }, [winnerRevealPayload]);

  const pickByRangeSection = useMemo(() => {
    if (!game?.numberRange) return null;
    const gMin = game.numberRange.min;
    const gMax = game.numberRange.max;
    const span = Math.max(1, gMax - gMin + 1);
    const parseLoHi = () => {
      const a = parseInt(String(rangeFromInput).trim(), 10);
      const b = parseInt(String(rangeToInput).trim(), 10);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      if (lo < gMin || hi > gMax) return null;
      return { lo, hi };
    };
    const parsed = parseLoHi();
    const barLeftPct = parsed ? ((parsed.lo - gMin) / span) * 100 : 0;
    const barWidthPct = parsed ? ((parsed.hi - parsed.lo + 1) / span) * 100 : 8;

    return (
      <div className="rounded-xl border border-hash-gold/15 bg-gradient-to-b from-dark-surface/40 to-dark-surface/25 p-4 shadow-inner shadow-black/20">
          <div className="mb-4">
            <div className="flex justify-between text-[10px] font-mono text-gray-500 tabular-nums mb-1.5 px-0.5">
              <span>{parsed ? parsed.lo : '—'}</span>
              <span className="text-hash-gold/70">{parsed ? `${parsed.hi - parsed.lo + 1} nums` : 'enter range'}</span>
              <span>{parsed ? parsed.hi : '—'}</span>
            </div>
            <div
              className="relative h-2.5 rounded-full border border-hash-gold/15 bg-dark/60 overflow-hidden"
              aria-hidden
            >
              <div
                className="absolute inset-y-0 rounded-full bg-gradient-to-r from-medora-blue/35 via-hash-gold/25 to-hash-gold/45 opacity-40"
                style={{ left: 0, width: '100%' }}
              />
              {parsed && (
                <div
                  className="absolute inset-y-0 rounded-full bg-gradient-to-r from-medora-blue/70 to-hash-gold/80 shadow-[0_0_12px_rgba(238,212,91,0.35)]"
                  style={{
                    left: `${barLeftPct}%`,
                    width: `${Math.max(barWidthPct, 100 / span)}%`,
                  }}
                />
              )}
            </div>
          </div>

          <div className="rounded-xl bg-dark-surface/50 border border-hash-gold/10 p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-gray-400 text-xs mb-1.5 block">From</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={gMin}
                  max={gMax}
                  value={rangeFromInput}
                  onChange={(e) => setRangeFromInput(e.target.value)}
                  disabled={isAtMax}
                  placeholder={String(gMin)}
                  className="w-full rounded-lg border border-hash-gold/15 bg-dark/50 px-3 py-2.5 text-center text-base font-bold font-mono text-white tabular-nums shadow-sm placeholder:text-gray-600 focus:border-hash-gold/40 focus:outline-none focus:ring-1 focus:ring-hash-gold/30 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Range from"
                />
              </label>
              <label className="block">
                <span className="text-gray-400 text-xs mb-1.5 block">To</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={gMin}
                  max={gMax}
                  value={rangeToInput}
                  onChange={(e) => setRangeToInput(e.target.value)}
                  disabled={isAtMax}
                  placeholder={String(gMax)}
                  className="w-full rounded-lg border border-hash-gold/15 bg-dark/50 px-3 py-2.5 text-center text-base font-bold font-mono text-white tabular-nums shadow-sm placeholder:text-gray-600 focus:border-hash-gold/40 focus:outline-none focus:ring-1 focus:ring-hash-gold/30 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Range to"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={handleApplyRange}
              disabled={isAtMax}
              className={`w-full py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${isAtMax
                  ? 'cursor-not-allowed border border-gray-600/30 bg-gray-700/50 text-gray-500'
                  : 'cursor-pointer border border-hash-gold/35 bg-hash-gold/15 text-hash-gold hover:border-hash-gold/50 hover:bg-hash-gold/25 hover:shadow-[0_0_16px_rgba(238,212,91,0.15)]'
                }`}
            >
              Add range to slip
            </button>
          </div>
      </div>
    );
  }, [
    game,
    isAtMax,
    rangeFromInput,
    rangeToInput,
    handleApplyRange,
  ]);

  const pickYourNumbersBody = useMemo(() => {
    if (!game) return null;

    const numbersPanel = (
      <div
        className="rounded-xl border border-medora-blue/30 bg-dark-surface/25 p-4 shadow-[inset_0_1px_0_rgba(56,189,248,0.06)]"
        {...(pickByRangeSection
          ? { role: 'tabpanel', id: 'pick-tabpanel-numbers', 'aria-labelledby': 'pick-tab-numbers' }
          : {})}
      >
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <p className="text-[11px] leading-snug text-gray-500 sm:max-w-md">
            Tap the grid or use random fills. Everything adds to the same slip.
          </p>
          <div className="flex flex-wrap justify-center gap-2 sm:shrink-0 sm:justify-end">
            <button
              type="button"
              onClick={handlePickRandom}
              disabled={isAtMax}
              className={`flex cursor-pointer items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${isAtMax
                  ? 'cursor-not-allowed border border-gray-600/30 bg-gray-700/50 text-gray-500'
                  : 'border border-medora-blue/20 bg-medora-blue/10 text-medora-blue hover:bg-medora-blue/20'
                }`}
            >
              <img src="/images/icon-random-dice.svg" alt="" className="h-3 w-3" />
              Random
            </button>
            <button
              type="button"
              onClick={handlePickAllRandom}
              className="flex cursor-pointer items-center gap-1 rounded-lg border border-hash-gold/20 bg-hash-gold/10 px-3 py-1.5 text-xs font-semibold text-hash-gold transition-colors hover:bg-hash-gold/20"
            >
              <img src="/images/icon-random-dice.svg" alt="" className="h-3 w-3" />
              All Random
            </button>
          </div>
        </div>
        {renderNumberPicker}
      </div>
    );

    if (!pickByRangeSection) {
      return numbersPanel;
    }

    const tabBtn = (id, isActive, label, tabId) => (
      <button
        key={tabId}
        type="button"
        role="tab"
        id={id}
        aria-selected={isActive}
        aria-controls={`pick-tabpanel-${tabId}`}
        tabIndex={isActive ? 0 : -1}
        onClick={() => setPickYourNumbersTab(tabId)}
        className={`flex-1 rounded-md px-3 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors sm:text-sm cursor-pointer ${isActive
            ? 'bg-hash-gold/20 text-hash-gold shadow-sm'
            : 'text-gray-400 hover:text-white'
          }`}
      >
        {label}
      </button>
    );

    return (
      <div className="space-y-4">
        <div
          className="flex rounded-lg border border-hash-gold/20 bg-dark-secondary/80 p-0.5"
          role="tablist"
          aria-label="Pick your numbers mode"
        >
          {tabBtn('pick-tab-numbers', pickYourNumbersTab === 'numbers', 'Pick by numbers', 'numbers')}

          {tabBtn('pick-tab-range', pickYourNumbersTab === 'range', 'Pick by range', 'range')}
        </div>

        {pickYourNumbersTab === 'range' && (
          <div
            className="rounded-xl border border-hash-gold/30 bg-dark-surface/25 p-4 shadow-[inset_0_1px_0_rgba(238,212,91,0.06)]"
            role="tabpanel"
            id="pick-tabpanel-range"
            aria-labelledby="pick-tab-range"
          >
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <p className="max-w-xl text-[11px] leading-snug text-gray-500">
                From–To inclusive; adds every free number in the range (skips slip and on-chain). At most{' '}
                {maxPicksPerBet} available numbers per purchase.
              </p>
              <span className="shrink-0 rounded-md border border-hash-gold/20 bg-dark-surface/60 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 tabular-nums">
                {game.numberRange.min}–{game.numberRange.max}
              </span>
            </div>
            {pickByRangeSection}
          </div>
        )}

        {pickYourNumbersTab === 'numbers' && numbersPanel}
      </div>
    );
  }, [
    game,
    maxPicksPerBet,
    pickByRangeSection,
    pickYourNumbersTab,
    renderNumberPicker,
    isAtMax,
    handlePickRandom,
    handlePickAllRandom,
  ]);

  if (!game) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-dark via-dark-secondary to-dark relative">
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
        style={{
          backgroundImage: 'url(/images/background-games.jpg)',
          opacity: 0.15,
        }}
      />
      <NavHeader />
      {popupNumber !== null && (
        <PickAnimationPopup number={formatGameNumber(activeGame, popupNumber)} onComplete={handlePopupComplete} />
      )}

      {creditWarning && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setCreditWarning(null);
          }}
        >
          <div
            className="glass-card-gold-border max-w-md w-full rounded-xl p-6 shadow-xl border border-hash-gold/30 bg-dark-secondary/95"
            role="dialog"
            aria-labelledby="credit-warning-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="credit-warning-title" className="text-lg font-bold text-white mb-3">
              Not enough credit
            </h2>
            <p className="text-gray-300 text-sm mb-2">
              This bet needs{' '}
              <span className="text-hash-gold font-semibold">{creditWarning.requiredUsd} USD</span> in vault credit.
            </p>
            <p className="text-gray-400 text-sm mb-6">
              Your available credit:{' '}
              <span className="text-white font-semibold">{creditWarning.availableUsd} USD</span>
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                type="button"
                className="px-4 py-2.5 rounded-lg border border-hash-gold/30 text-gray-300 text-sm font-semibold hover:bg-dark-surface/80 transition-colors"
                onClick={() => setCreditWarning(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn-gold px-4 py-2.5 text-sm font-semibold rounded-lg"
                onClick={() => {
                  setCreditWarning(null);
                  navigate('/credit');
                }}
              >
                Add credit
              </button>
            </div>
          </div>
        </div>
      )}

      {generatingWinner && (
        <div
          className="fixed inset-0 z-[103] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="winner-reveal-title"
          aria-live="polite"
          aria-busy={winnerRevealBusy}
        >
          <div
            className={`glass-card-gold-border relative w-full overflow-hidden rounded-2xl border border-hash-gold/25 bg-dark-secondary/95 shadow-gold-glow ${winnerRevealBusy ? 'max-w-md' : 'max-w-lg'} max-h-[90vh] flex flex-col`}
            style={{
              backgroundImage:
                'radial-gradient(ellipse 120% 80% at 50% -20%, rgba(238, 212, 91, 0.12), transparent 55%)',
            }}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-hash-gold/50 to-transparent" />
            <button
              type="button"
              className="absolute right-3 top-3 z-10 rounded-lg border border-hash-gold/25 bg-dark-surface/90 px-2.5 py-1 text-xs font-semibold text-gray-300 hover:bg-dark-surface hover:text-white cursor-pointer"
              onClick={closeWinnerRevealModal}
            >
              Close
            </button>

            <div className="overflow-y-auto px-6 pb-8 pt-10 sm:px-8">
              <div className="text-center">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-hash-gold/80">Round complete</p>
                {winnerRevealMeta && (
                  <div className="mt-2 flex flex-col items-center gap-0.5 text-sm sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-2 sm:gap-y-0.5">
                    <span className="font-semibold text-medora-blue">{winnerRevealMeta.gameName}</span>
                    <span className="hidden text-gray-600 sm:inline" aria-hidden>
                      ·
                    </span>
                    <span className="text-gray-400">
                      Round  {' '}
                      <span className="font-mono font-semibold tabular-nums text-white">
                        {winnerRevealMeta.roundId != null && Number.isFinite(winnerRevealMeta.roundId)
                          ? winnerRevealMeta.roundId
                          : '—'}
                      </span>
                    </span>
                  </div>
                )}
                <h2 id="winner-reveal-title" className="mt-2 font-display text-xl font-bold tracking-tight text-white sm:text-2xl">
                  {winnerRevealBusy ? 'Generating winner' : 'Draw payout'}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-gray-400">
                  {winnerRevealBusy
                    ? 'Sealing the draw with our servers, then loading vault payout data from chain.'
                    : 'Vault credit events for this payout transaction (largest amount treated as main winner).'}
                </p>
              </div>

              {winnerRevealBusy && (
                <div className="mt-8">
                  <div className="h-1 w-full overflow-hidden rounded-full bg-dark-surface/80 ring-1 ring-hash-gold/15">
                    <div
                      className="h-full w-full animate-shimmer opacity-95"
                      style={{
                        backgroundImage:
                          'linear-gradient(90deg, transparent 0%, rgba(238, 212, 91, 0.2) 30%, #eed45b 50%, rgba(238, 212, 91, 0.2) 70%, transparent 100%)',
                        backgroundSize: '200% 100%',
                      }}
                    />
                  </div>
                  <p className="mt-3 text-center text-xs text-gray-500">Processing…</p>
                </div>
              )}

              {!winnerRevealBusy && winnerRevealPayload && (
                <>
                  {winnerRevealPayload.chainError && winnerRevealPayload.allRows.length === 0 && (
                    <p className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-center text-xs text-amber-100/90">
                      Could not load receipt data: {String(winnerRevealPayload.chainError)}
                    </p>
                  )}
                  {!winnerRevealPayload.apiOk && (
                    <p className="mt-3 text-center text-xs text-amber-200/80">Generate-winner API returned a non-success status; on-chain data below may still be useful.</p>
                  )}

                  <div className="mt-6 flex flex-wrap gap-1 rounded-xl border border-hash-gold/15 bg-dark-surface/50 p-1">
                    {[
                      { id: 'winner', label: 'Winner' },
                      {
                        id: 'neighbors',
                        label: `Neighbors (${winnerRevealPayload.neighbors?.length ?? 0})`,
                      },
                      { id: 'transaction', label: 'Transaction' },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setWinnerDetailTab(tab.id)}
                        className={`min-h-[2.25rem] flex-1 rounded-lg px-2 py-1.5 text-center text-xs font-semibold transition-colors sm:text-sm ${
                          winnerDetailTab === tab.id
                            ? 'bg-hash-gold/20 text-hash-gold shadow-sm'
                            : 'text-gray-400 hover:bg-dark-surface/80 hover:text-gray-200'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className="mt-5 min-h-[8rem]">
                    {winnerDetailTab === 'winner' && (
                      <div className="space-y-3 text-left">
                        {winnerRevealPayload.primary ? (
                          <>
                            <div className="rounded-xl border border-hash-gold/15 bg-dark-surface/60 px-4 py-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                                    Winner address
                                  </div>
                                  <div className="mt-1 break-all font-mono text-sm text-white">
                                    {winnerRevealPayload.primary.walletAddress}
                                  </div>
                                  <div className="mt-0.5 text-xs text-hash-gold/90">{winnerRevealPayload.primary.displayName}</div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    copyToClipboard(
                                      winnerRevealPayload.primary.walletAddress,
                                      'Winner address copied',
                                    )
                                  }
                                  className="shrink-0 rounded-lg border border-hash-gold/30 bg-hash-gold/10 px-2.5 py-1.5 text-[11px] font-semibold text-hash-gold hover:bg-hash-gold/20"
                                >
                                  Copy
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="rounded-xl border border-hash-gold/15 bg-dark-surface/60 px-3 py-2.5">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Amount (USDT)</div>
                                <div className="mt-1 text-lg font-bold tabular-nums text-hash-gold">
                                  {(() => {
                                    try {
                                      return Number(
                                        ethers.utils.formatUnits(
                                          String(winnerRevealPayload.primary.rewardWeiStr || '0'),
                                          18,
                                        ),
                                      ).toLocaleString(undefined, { maximumFractionDigits: 6 });
                                    } catch {
                                      return '—';
                                    }
                                  })()}
                                </div>
                              </div>
                              <div className="rounded-xl border border-hash-gold/15 bg-dark-surface/60 px-3 py-2.5">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Paid Amounts (USDT)</div>
                                <div className="mt-1 text-lg font-bold tabular-nums text-white">
                                  {Number(winnerRevealPayload.primary.prize ?? 0).toLocaleString(undefined, {
                                    maximumFractionDigits: 4,
                                  })}
                                </div>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500">
                              {winnerRevealPayload.primary.gameRoundLabel} · {winnerRevealPayload.primary.gameName}
                            </p>
                          </>
                        ) : (
                          <p className="text-center text-sm text-gray-400">
                            No vault winner log with a non-zero payout was found for this transaction. If payouts are split across txs, open the payout hash on BscScan or wait for indexing.
                          </p>
                        )}
                      </div>
                    )}

                    {winnerDetailTab === 'neighbors' && (
                      <div className="max-h-56 overflow-auto rounded-xl border border-hash-gold/10">
                        {(winnerRevealPayload.neighbors?.length ?? 0) === 0 ? (
                          <p className="p-4 text-center text-sm text-gray-400">No neighbor payout rows in this receipt.</p>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() =>
                                  copyToClipboard(
                                    winnerRevealPayload.neighbors.map((r) => r.walletAddress).join('\n'),
                                    'Neighbor addresses copied',
                                  )
                                }
                                className="rounded-lg border border-hash-gold/30 bg-hash-gold/10 px-2.5 py-1.5 text-[11px] font-semibold text-hash-gold hover:bg-hash-gold/20"
                              >
                                Copy all addresses
                              </button>
                            </div>
                            <div className="max-h-56 overflow-auto rounded-xl border border-hash-gold/10">
                              <table className="w-full text-left text-xs sm:text-sm">
                                <thead className="sticky top-0 z-[1] bg-dark-surface/95 text-[10px] uppercase tracking-wider text-gray-500">
                                  <tr>
                                    <th className="px-3 py-2 font-semibold">Address</th>
                                    <th className="px-3 py-2 font-semibold text-right">USDT</th>
                                    <th className="w-px whitespace-nowrap px-3 py-2 text-right font-semibold">Copy</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {winnerRevealPayload.neighbors.map((row, idx) => (
                                    <tr key={`${row.walletAddress}-${idx}`} className="border-t border-hash-gold/10">
                                      <td className="max-w-0 px-3 py-2">
                                        <div className="truncate font-mono text-gray-200" title={row.walletAddress}>
                                          {row.displayName}
                                        </div>
                                      </td>
                                      <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums text-hash-gold/90">
                                        {(() => {
                                          try {
                                            return Number(
                                              ethers.utils.formatUnits(String(row.rewardWeiStr || '0'), 18),
                                            ).toLocaleString(undefined, {
                                              maximumFractionDigits: 6,
                                            });
                                          } catch {
                                            return '—';
                                          }
                                        })()}
                                      </td>
                                      <td className="px-2 py-2 text-right">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            copyToClipboard(row.walletAddress, 'Neighbor address copied')
                                          }
                                          className="rounded-md border border-hash-gold/25 px-2 py-1 text-[10px] font-semibold text-hash-gold hover:bg-hash-gold/10"
                                        >
                                          Copy
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {winnerDetailTab === 'transaction' && (
                      <div className="space-y-4 text-left">
                        <div className="rounded-xl border border-hash-gold/15 bg-dark-surface/60 px-3 py-2.5">
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Receipt queried</div>
                          <a
                            href={`${bscExplorerBase}/tx/${winnerRevealPayload.payoutTxHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 block break-all font-mono text-xs text-medora-blue hover:underline"
                          >
                            {winnerRevealPayload.payoutTxHash}
                          </a>
                          {winnerRevealPayload.buyTxHash &&
                            winnerRevealPayload.buyTxHash.toLowerCase() !==
                              winnerRevealPayload.payoutTxHash.toLowerCase() && (
                              <div className="mt-3 border-t border-hash-gold/10 pt-2">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Your buy tx</div>
                                <a
                                  href={`${bscExplorerBase}/tx/${winnerRevealPayload.buyTxHash}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-1 block break-all font-mono text-xs text-gray-400 hover:text-medora-blue hover:underline"
                                >
                                  {winnerRevealPayload.buyTxHash}
                                </a>
                              </div>
                            )}
                        </div>
                        <div className="rounded-xl border border-hash-gold/10 bg-dark-surface/40 px-3 py-2 text-[10px] leading-relaxed text-gray-500">
                          <div>
                            <span className="font-semibold text-gray-400">Vault</span>{' '}
                            <span className="font-mono text-gray-400">{coreVaultAddress}</span>
                          </div>
                          <div className="mt-1 break-all">
                            <span className="font-semibold text-gray-400">topic0</span> {VAULT_WINNER_EVENT_TOPIC0}
                          </div>
                        </div>
                        <div>
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Tab-separated (copy)</span>
                            <button
                              type="button"
                              disabled={!winnerRevealTsv}
                              onClick={() => {
                                if (!winnerRevealTsv) return;
                                navigator.clipboard.writeText(winnerRevealTsv).then(
                                  () => toast.success('Copied to clipboard'),
                                  () => toast.error('Copy failed'),
                                );
                              }}
                              className="rounded-md border border-hash-gold/25 px-2 py-1 text-[10px] font-semibold text-hash-gold hover:bg-hash-gold/10 disabled:opacity-40"
                            >
                              Copy
                            </button>
                          </div>
                          <textarea
                            readOnly
                            value={winnerRevealTsv || '(no rows)'}
                            rows={Math.min(10, 2 + (winnerRevealPayload.allRows?.length || 0))}
                            className="w-full resize-y rounded-lg border border-hash-gold/15 bg-dark px-2 py-2 font-mono text-[11px] leading-relaxed text-gray-200"
                            spellCheck={false}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="pt-nav pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container-premium max-w-7xl mx-auto page-top-spacing">
          <GameSelector
            activeGame={activeGame}
            onSelect={(gameId) => {
              if (isGamePlayEnabled(gameId)) setActiveGame(gameId);
            }}
          />
          <div className="mt-6 mb-6">
            <GameInfoPanel
              game={game}
              roundId={roundId}
              soldTickets={soldTickets}
              roundLoading={roundLoading}
              roundError={roundError}
              vaultCreditUsdLabel={vaultCreditUsdLabel}
              vaultCreditLoading={vaultCreditLoading}
              walletConnected={Boolean(address)}
            />
          </div>

          {/* Desktop 3-panel layout */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-6">
            {/* Left panel: selected numbers */}
            <div className="col-span-3">
              <div className="glass-card-gold-border p-5 rounded-xl h-full flex flex-col">
                <div className="mb-4 text-center">
                  <h3 className="text-lg font-bold text-white text-shadow-dark">Your Picked</h3>
                  <p className="mt-1 text-xs text-gray-400">
                    Your tickets this round:{' '}
                    <span className="font-semibold text-medora-blue tabular-nums">{onChainTicketsLabel}</span>
                  </p>
                </div>
                <div className={`flex-1 flex flex-wrap content-start justify-center gap-3 overflow-visible ${isSummit ? 'grid grid-cols-2' : ''}`}>
                  {selectedNumbers.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center w-full self-center">No numbers selected</p>
                  ) : (
                    selectedNumbers.map((num) => (
                      <div
                        key={num}
                        className={`relative rounded-xl bg-gradient-to-br from-hash-gold/30 to-hash-gold-dark/40 border-2 border-hash-gold/60 flex items-center justify-center shadow-[0_0_15px_rgba(238,212,91,0.3)] cursor-pointer hover:shadow-[0_0_25px_rgba(238,212,91,0.5)] transition-shadow ${isSummit ? 'w-24 h-24 sm:w-28 sm:h-28' : 'w-16 h-16 sm:w-[72px] sm:h-[72px]'
                          }`}
                        onClick={() => handleRemoveNumber(num)}
                      >
                        <span className="text-xl sm:text-2xl font-bold text-white drop-shadow-lg">
                          {formatGameNumber(activeGame, num)}
                        </span>
                        <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500/90 flex items-center justify-center shadow-lg">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {onChainGetNumbersSection}
                <div className="mt-4 pt-4 border-t border-hash-gold/10">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">{picksThisBetCount} / {game.maxNumbersPerTicket}</span>
                    <span className="text-hash-gold font-bold">{totalCost.toFixed(2)} USD</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Center panel: number picker */}
            <div className="col-span-6">
              <div className="glass-card-gold-border p-5 rounded-xl">
                <div className="mb-3 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-white text-shadow-dark">Pick Your Numbers</h3>
                    <button
                      onClick={handleClearNumbers}
                      className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-semibold hover:bg-red-500/20 transition-colors flex items-center gap-1 cursor-pointer"
                    >
                      <img src="/images/icon-clear.svg" alt="" className="w-3 h-3" />
                      Clear
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-500">
                    Use the tabs below — range or grid — picks combine on your slip.
                  </p>
                </div>
                {pickYourNumbersBody}
              </div>
            </div>

            {/* Right panel: actions */}
            <div className="col-span-3">
              <div className="glass-card-blue-border p-5 rounded-xl h-full flex flex-col">
                <h3 className="text-lg font-bold text-white mb-4 text-center text-shadow-dark">Bet</h3>
                <div className="flex-1 space-y-4">
                  <div className="p-4 rounded-xl bg-dark-surface/50 border border-hash-gold/10">
                    <div className="text-gray-400 text-xs mb-1">Entry Fee</div>
                    <div className="text-white font-bold text-lg">{game.entryFee} USD</div>
                  </div>
                  <div className="p-4 rounded-xl bg-dark-surface/50 border border-hash-gold/10">
                    <div className="text-gray-400 text-xs mb-1">Max Prize</div>
                    <div className="text-hash-gold font-bold text-lg">{game.maxPrize.toLocaleString()} USD</div>
                  </div>
                  <div className="p-4 rounded-xl bg-dark-surface/50 border border-hash-gold/10">
                    <div className="text-gray-400 text-xs mb-1">Numbers</div>
                    <div className="text-white font-bold text-lg">{picksThisBetCount} / {game.maxNumbersPerTicket}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-dark-surface/50 border border-hash-gold/20">
                    <div className="text-gray-400 text-xs mb-1">Total Cost</div>
                    <div className="text-hash-gold font-bold text-2xl drop-shadow-[0_0_10px_rgba(238,212,91,0.5)]">{totalCost.toFixed(2)} USD</div>
                  </div>
                </div>
                <button
                  onClick={handlePlaceBet}
                  disabled={
                    !canPlayActiveGame ||
                    selectedNumbers.length === 0 ||
                    placing ||
                    syncingRoundOutcome ||
                    maxPicksPerBet <= 0
                  }
                  className={`w-full mt-4 py-3.5 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${canPlayActiveGame && selectedNumbers.length > 0 && !placing && !syncingRoundOutcome && maxPicksPerBet > 0
                      ? 'btn-gold hover:shadow-gold-glow-lg'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  {!canPlayActiveGame ? (
                    <>
                      <img src="/images/icon-ticket.svg" alt="" className="w-5 h-5 opacity-50" />
                      Coming soon
                    </>
                  ) : placing ? (
                    <>
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Placing...
                    </>
                  ) : syncingRoundOutcome ? (
                    <>
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Checking round…
                    </>
                  ) : !address ? (
                    <>
                      <img src="/images/icon-wallet-connect.svg" alt="" className="w-5 h-5" />
                      Connect
                    </>
                  ) : (
                    <>
                      <img src="/images/icon-ticket.svg" alt="" className="w-5 h-5" />
                      Place Bet
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile single column */}
          <div className="lg:hidden space-y-4">
            {/* Number picker */}
            <div className="glass-card-gold-border p-5 rounded-xl">
              <div className="mb-3 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white text-shadow-dark">Pick Your Numbers</h3>
                  <button
                    onClick={handleClearNumbers}
                    className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-semibold hover:bg-red-500/20 transition-colors flex items-center gap-1 cursor-pointer"
                  >
                    <img src="/images/icon-clear.svg" alt="" className="w-3 h-3" />
                    Clear
                  </button>
                </div>
                <p className="text-[11px] text-gray-500">
                  Use the tabs below — range or grid — picks combine on your slip.
                </p>
              </div>
              {pickYourNumbersBody}
            </div>

            {/* Mobile picked numbers grid */}
            <div className="glass-card-gold-border p-4 rounded-xl">
              <div className="mb-3 text-center">
                <h3 className="text-base font-bold text-white text-shadow-dark">Your Picked</h3>
                <p className="mt-1 text-[11px] text-gray-400">
                  Tickets this round:{' '}
                  <span className="font-semibold text-medora-blue tabular-nums">{onChainTicketsLabel}</span>
                </p>
              </div>
              <div className={`grid gap-2 mb-3 ${isSummit ? 'grid-cols-3' : 'grid-cols-5'}`}>
                {selectedNumbers.length === 0 ? (
                  <p className={`text-gray-500 text-sm text-center py-4 ${isSummit ? 'col-span-3' : 'col-span-5'}`}>No numbers selected</p>
                ) : (
                  selectedNumbers.map((num) => (
                    <div
                      key={num}
                      className={`relative aspect-square rounded-lg bg-gradient-to-br from-hash-gold/30 to-hash-gold-dark/40 border border-hash-gold/60 flex items-center justify-center shadow-[0_0_10px_rgba(238,212,91,0.2)] cursor-pointer active:shadow-[0_0_20px_rgba(238,212,91,0.4)] transition-shadow`}
                      onClick={() => handleRemoveNumber(num)}
                    >
                      <span className={`font-bold text-white drop-shadow-lg ${isSummit ? 'text-base' : 'text-sm'}`}>
                        {formatGameNumber(activeGame, num)}
                      </span>
                      <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500/90 flex items-center justify-center shadow-sm">
                        <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {onChainGetNumbersSection}
              <div className="flex items-center justify-between pt-3 border-t border-hash-gold/10">
                <div className="flex gap-2">
                  <button
                    onClick={handlePickRandom}
                    disabled={isAtMax}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${isAtMax
                        ? 'bg-gray-700/50 border border-gray-600/30 text-gray-500 cursor-not-allowed'
                        : 'bg-medora-blue/10 border border-medora-blue/20 text-medora-blue'
                      }`}
                  >
                    +1 Random
                  </button>
                  <button
                    onClick={handlePickAllRandom}
                    className="px-3 py-1.5 rounded-lg bg-hash-gold/10 border border-hash-gold/20 text-hash-gold text-xs font-semibold"
                  >
                    All Random
                  </button>
                </div>
                <div className="text-right">
                  <div className="text-hash-gold font-bold">{totalCost.toFixed(2)} USD</div>
                  <div className="text-gray-500 text-xs">{picksThisBetCount} / {game.maxNumbersPerTicket}</div>
                </div>
              </div>
              <button
                onClick={handlePlaceBet}
                disabled={
                  !canPlayActiveGame ||
                  selectedNumbers.length === 0 ||
                  placing ||
                  syncingRoundOutcome ||
                  maxPicksPerBet <= 0
                }
                className={`w-full mt-3 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${canPlayActiveGame && selectedNumbers.length > 0 && !placing && !syncingRoundOutcome && maxPicksPerBet > 0
                    ? 'btn-gold'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
              >
                {!canPlayActiveGame
                  ? 'Coming soon'
                  : placing
                    ? 'Placing...'
                    : syncingRoundOutcome
                      ? 'Checking round…'
                      : !address
                        ? 'Connect'
                        : 'Place Bet'}
              </button>
            </div>
          </div>
        </div>
      </main>

      <FooterSection />
    </div>
  );
}

export default Games;
